// AllReleases component
import React, { useState, useEffect } from 'react';
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import DetailDialog, {
  DetailSection,
  DetailFieldGrid,
  DetailHeaderPhotos,
  money,
  listFilterFieldSx,
  listTableHeadSx,
  listActionBtnSx,
} from '../common/DetailDialog';
import PhotoThumb from '../common/PhotoThumb';
import baseURL from '../../baseURL';

const releaseClientPhoto = (release) =>
  release?.clientPhoto
  || release?.releaseCustomerPhoto
  || release?.customerPhoto
  || release?.pledgeId?.clientPhoto;

const releaseArticlePhoto = (release) =>
  release?.articlePhoto
  || release?.releaseArticlePhoto
  || release?.pledgeId?.articlePhoto;

const releaseCustomerName = (release) => {
  if (release?.client?.name) return release.client.name;
  const c = release?.pledgeId?.customer;
  if (c) return `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'N/A';
  return 'N/A';
};

/** All Releases list */
const AllReleases = () => {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [releaseToDelete, setReleaseToDelete] = useState(null);

  useEffect(() => {
    fetchReleases();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, dateFrom, dateTo, releases]);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/releases`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch releases');
      }

      if (data.success) {
        setReleases(data.data || []);
        setFilteredReleases(data.data || []);  
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
      } else {
        setReleases([]);
        setFilteredReleases([]);
      }
    } catch (error) {
      setReleases([]);
      setFilteredReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    applyFilters();
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
    applyFilters();
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = () => {
    let filtered = [...releases];

    if (searchTerm) {
      const searchRegex = new RegExp(searchTerm, 'i');
      filtered = filtered.filter(release => 
        searchRegex.test(release.ticketNo || '') ||
        searchRegex.test(release.pledgeId?.loanDetails?.ticketNo || '') ||
        searchRegex.test(release.client?.name || '') ||
        searchRegex.test(release.client?.code || '') ||
        searchRegex.test(String(release.calculations?.total || ''))
      );
    }

    if (dateFrom || dateTo) {
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;
      
      filtered = filtered.filter(release => {
        const releaseDate = new Date(release.releaseDate);
        if (fromDate && releaseDate < fromDate) return false;
        if (toDate && releaseDate > toDate) return false;
        return true;
      });
    }

    setFilteredReleases(filtered);
  };

  const handleViewDetails = (release) => {
    setSelectedRelease(release);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleEdit = (release) => {
    navigate(`/release?edit=${release._id}`);
  };

  const handleDelete = (release) => {
    setReleaseToDelete(release);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!releaseToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/releases/${releaseToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('Release deleted successfully. Accounting entries have been reversed and pledge status updated.');
        fetchReleases();
        setDeleteConfirmOpen(false);
        setReleaseToDelete(null);
      } else {
        alert(data.message || 'Failed to delete release');
      }
    } catch (error) {
      alert('Error deleting release: ' + error.message);
    }
  };

  const handlePrint = async (release) => {
    try {
      // TODO: Create ReleasePrint template
      alert('Print functionality for releases will be available soon.');
      // const { printRelease } = await import('../../utils/printUtility');
      // await printRelease(release);
    } catch (error) {
      alert('Error printing release. Please try again.');
    }
  };

  return (
    <>
      <GirviPageShell
        title="All Releases"
        subtitle="Pledge · Releases"
        chipLabel="Pledge"
        maxWidth="xl"
        compact
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Ticket no, customer name, or amount"
                sx={listFilterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={listTableHeadSx}>Date</TableCell>
                  <TableCell sx={listTableHeadSx}>Ticket</TableCell>
                  <TableCell sx={listTableHeadSx}>Photos</TableCell>
                  <TableCell sx={listTableHeadSx}>Customer</TableCell>
                  <TableCell sx={listTableHeadSx} align="right">Principal</TableCell>
                  <TableCell sx={listTableHeadSx} align="right">Interest</TableCell>
                  <TableCell sx={listTableHeadSx} align="right">Total</TableCell>
                  <TableCell sx={listTableHeadSx}>Status</TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReleases.length > 0 ? (
                  filteredReleases.map((release) => {
                    const ticketNo = release.ticketNo || release.pledgeId?.loanDetails?.ticketNo || 'N/A';
                    const status = release.status || 'N/A';
                    return (
                      <TableRow
                        key={release._id}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
                          {formatDate(release.releaseDate)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={ticketNo}
                            sx={{
                              fontWeight: 800,
                              bgcolor: 'rgba(201,162,77,0.14)',
                              color: '#8a6d2f',
                              border: '1px solid rgba(201,162,77,0.35)',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhotoThumb src={releaseClientPhoto(release)} label="Client" size={36} />
                            <PhotoThumb src={releaseArticlePhoto(release)} label="Article" size={36} />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {releaseCustomerName(release)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {money(release.calculations?.principle)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {money(release.calculations?.interest)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                          {money(release.calculations?.total)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(status).toUpperCase()}
                            sx={{
                              height: 24,
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              bgcolor:
                                String(status).toUpperCase() === 'RELEASED'
                                  ? 'rgba(74,222,128,0.18)'
                                  : 'rgba(148,163,184,0.18)',
                              color:
                                String(status).toUpperCase() === 'RELEASED'
                                  ? '#15803d'
                                  : '#475569',
                              border: '1px solid #e2e8f0',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View">
                              <IconButton onClick={() => handleViewDetails(release)} sx={listActionBtnSx} size="small">
                                <VisibilityIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton onClick={() => handleEdit(release)} sx={listActionBtnSx} size="small">
                                <EditIcon sx={{ fontSize: 18, color: '#15803d' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDelete(release)} sx={listActionBtnSx} size="small">
                                <DeleteIcon sx={{ fontSize: 18, color: '#b91c1c' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print">
                              <IconButton onClick={() => handlePrint(release)} sx={listActionBtnSx} size="small">
                                <PrintIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No releases found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GirviPageShell>

      <DetailDialog
        open={openDetails}
        onClose={handleCloseDetails}
        title={selectedRelease?.ticketNo || selectedRelease?.pledgeId?.loanDetails?.ticketNo || '—'}
        subtitle="Release details"
        status={selectedRelease?.status}
        headerPhotos={
          <DetailHeaderPhotos
            clientPhoto={releaseClientPhoto(selectedRelease)}
            articlePhoto={releaseArticlePhoto(selectedRelease)}
          />
        }
      >
        {selectedRelease && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailSection title="Release info">
                  <DetailFieldGrid
                    items={[
                      {
                        label: 'Ticket No',
                        value: selectedRelease.ticketNo || selectedRelease.pledgeId?.loanDetails?.ticketNo || 'N/A',
                      },
                      { label: 'Release Date', value: formatDate(selectedRelease.releaseDate) },
                      { label: 'Pawn Date', value: formatDate(selectedRelease.pawnDate) },
                      { label: 'Months', value: selectedRelease.months || 'N/A' },
                      { label: 'Status', value: selectedRelease.status || 'N/A' },
                    ]}
                  />
                </DetailSection>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailSection title="Customer">
                  <DetailFieldGrid
                    items={[
                      {
                        label: 'Customer Code',
                        value: selectedRelease.client?.code || selectedRelease.pledgeId?.customer?.code || 'N/A',
                      },
                      {
                        label: 'Name',
                        value: releaseCustomerName(selectedRelease),
                      },
                    ]}
                  />
                </DetailSection>
              </Grid>
            </Grid>

            <DetailSection title="Calculations">
              <DetailFieldGrid
                items={[
                  { label: 'Rate of Interest', value: `${selectedRelease.primaryRate || '0'}%` },
                  { label: 'Principal', value: money(selectedRelease.calculations?.principle) },
                  { label: 'Interest', value: money(selectedRelease.calculations?.interest) },
                  { label: 'Total Amount', value: money(selectedRelease.calculations?.total) },
                ]}
              />
            </DetailSection>
          </>
        )}
      </DetailDialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this release? This action cannot be undone.
            The accounting entries will be reversed and the pledge status will be changed from RELEASED to ACTIVE.
            {releaseToDelete && (
              <Box sx={{ mt: 1.5 }}>
                <strong>Ticket No:</strong> {releaseToDelete.ticketNo || releaseToDelete.pledgeId?.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Release Date:</strong> {formatDate(releaseToDelete.releaseDate)}<br />
                <strong>Total Amount:</strong> {money(releaseToDelete.calculations?.total)}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllReleases;
